import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import './Sidebar.css';
import {
  FiHome, FiCompass, FiZap, FiActivity, FiBarChart2, FiSettings,
  FiTerminal, FiMoon, FiSun, FiCreditCard, FiShield, FiFileText, FiCheckSquare, FiBookOpen, FiUser, FiLogOut
} from 'react-icons/fi';

const NavGroup = ({ label, items }) => (
  <div className="nav-group">
    <div className="nav-group-label">{label}</div>
    <ul className="nav-list">
      {items.map(item => (
        <li key={item.path}>
          <NavLink id={item.id} to={item.path} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            {item.icon}<span>{item.name}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  </div>
);

const Sidebar = () => {
  const { user, isLocalExecution, logout } = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const hasScannerAccess = isLocalExecution || Boolean(user?.scanner_access);
  const hasMethodologyAccess = isLocalExecution || Boolean(user?.methodology_access);
  const groups = [];

  if (isLocalExecution) {
    groups.push(
      { label: 'Workspace', items: [
        { path: '/today', id: 'nav-today', icon: <FiHome />, name: 'Today' },
        { path: '/trade-plans', id: 'nav-plans', icon: <FiFileText />, name: 'Plans' },
        { path: '/market-intelligence', id: 'nav-market-intelligence', icon: <FiCompass />, name: 'Intelligence' },
      ] },
      { label: 'Execute', items: [
        { path: '/control-deck', icon: <FiZap />, name: 'Execution' },
        { path: '/performance', icon: <FiActivity />, name: 'Live Positions' },
      ]},
      { label: 'Research', items: [
        { path: '/analytics', id: 'nav-analytics', icon: <FiBarChart2 />, name: 'Trade Research' },
        { path: '/research-validation', id: 'nav-research-validation', icon: <FiCheckSquare />, name: 'Filter Validation' },
      ] },
      { label: 'System', items: [
        { path: '/workspace-settings', icon: <FiSettings />, name: 'Settings' },
        { path: '/logs', icon: <FiTerminal />, name: 'Diagnostics' },
      ]},
    );
  } else if (hasScannerAccess) {
    groups.push({
      label: 'AlgoStock',
      items: [
        { path: '/today', id: 'nav-today', icon: <FiHome />, name: 'Today' },
        { path: '/market-intelligence', id: 'nav-market-intelligence', icon: <FiCompass />, name: 'AlgoStock Scanner' },
        { path: '/trade-plans', id: 'nav-plans', icon: <FiFileText />, name: 'Trade Plans' },
      ],
    });
  }

  if (hasMethodologyAccess) {
    groups.push({ label: 'Framework', items: [
      { path: '/methodology', id: 'nav-methodology', icon: <FiBookOpen />, name: 'Trading Framework' },
    ]});
  }

  if (!isLocalExecution) {
    groups.push({ label: 'Account', items: [
      { path: '/account', icon: <FiUser />, name: 'My Account' },
      { path: '/billing', icon: <FiCreditCard />, name: 'Plan & Billing' },
    ] });
    if (user?.role === 'admin') groups.push({ label: 'Admin', items: [{ path: '/admin', icon: <FiShield />, name: 'Admin' }] });
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-main">
        <div className="sidebar-brand">
          <svg className="sidebar-logo" viewBox="0 0 48 48" aria-label="AlgoStock" role="img">
            <rect width="48" height="48" rx="13" fill="#0A0F18" />
            <path d="M11 34.5L22.6 12.5C23.2 11.4 24.8 11.4 25.4 12.5L37 34.5" fill="none" stroke="#F7FAFF" strokeWidth="4.2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M16 29.2H25.2L29.3 22.6L37 16.9" fill="none" stroke="#4D8DFF" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M33.7 14.9L37.9 16.2L36.7 20.5" fill="none" stroke="#4D8DFF" strokeWidth="3.1" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div>
            <div className="brand-name">ALGOSTOCK</div>
            <div className="brand-edition">{isLocalExecution ? 'RESEARCH WORKSTATION' : 'CLOUD PLATFORM'}</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {groups.map(group => <NavGroup key={group.label} {...group} />)}
        </nav>
      </div>

      <div className="sidebar-footer">
        {!isLocalExecution && <div style={{fontSize:'0.75rem', color:'var(--text-secondary)', marginBottom:'.6rem', overflow:'hidden', textOverflow:'ellipsis'}}>{user?.email}</div>}
        <button className="theme-switch" onClick={toggleTheme}>
          {theme === 'light' ? <FiMoon /> : <FiSun />}
          <span>{theme === 'light' ? 'Dark mode' : 'Light mode'}</span>
        </button>
        {!isLocalExecution && <button className="theme-switch" onClick={logout} style={{marginTop:'.4rem'}}><FiLogOut/><span>Logout</span></button>}
      </div>
    </aside>
  );
};

export default Sidebar;
